import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import authNavigate from "../../../globalComponents/AuthNav";
import ErrorPopup from "../../../utils/popup/ErrorPopup";
import ProcessingPopup from "../../../utils/popup/ProcessingPopup";
import SuccessPopup from "../../../utils/popup/SuccessPopup";
import PaginationControls from "../../../utils/PaginationControls";
import DeleteConfirmationModal from "../../../utils/DeleteConfirmationModal";
import TrimContent from "../../../utils/TrimContent";
import { UserCircleDisplay } from "../../../utils/UserCustomDisplay";
import { useTimezone } from "../../../utils/TimezoneContext";

const STATUS_CONFIG = {
    RUNNING:   { label: "Running",   badgeClass: "ev-badge-orange" },
    COMPLETED: { label: "Completed", badgeClass: "ev-badge-green"  },
    FAILED:    { label: "Failed",    badgeClass: "ev-badge-red"    },
};

const STATUS_OPTIONS = [
    { id: "ALL", label: "All" },
    ...Object.entries(STATUS_CONFIG).map(([id, { label }]) => ({ id, label })),
];

const StatusFilter = ({ value, onChange }) => {
    const [open, setOpen] = useState(false);
    const ref = React.useRef(null);

    React.useEffect(() => {
        const handler = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    return (
        <div className="position-relative" ref={ref}>
            <div
                className="d-flex align-items-center gap-1 cursor-pointer"
                onClick={() => setOpen((p) => !p)}
            >
                <label className="cursor-pointer">Status</label>
                <i className="bi bi-funnel-fill ev-fs-11"></i>
            </div>
            {open && (
                <div className="ev-card-8 position-absolute top-100 end-0 mt-1 p-2 ev-radius-xsmall d-flex flex-column"
                    style={{ zIndex: 9999, minWidth: "150px" }}>
                    {STATUS_OPTIONS.map((opt) => (
                        <button
                            key={opt.id}
                            type="button"
                            className={`w-100 text-start ev-radius-xsmall ev-btn-small mb-1 ${
                                value === opt.id
                                    ? "ev-btn-blue-fill-round"
                                    : "ev-btn-light-fill ev-reg-p-blue"
                            }`}
                            onClick={() => { onChange(opt.id); setOpen(false); }}
                        >
                            {opt.label}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

const GenAIRightsList = ({
    checks = [],
    total = 0,
    page = 1,
    setPage,
    size = 25,
    setSize,
    search = "",
    setSearch,
    onRefresh,
    onOpenDashboard,
    onDeleteCheck,
}) => {
    const navigate = useNavigate();
    const { formatDateTime } = useTimezone();

    const [searchInput, setSearchInput]       = useState(search);
    const [statusFilter, setStatusFilter]     = useState("ALL");
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [checkToDelete, setCheckToDelete]   = useState(null);

    const [isProcessingPopupVisible, setIsProcessingPopupVisible] = useState(false);
    const [processingMessage, setProcessingMessage] = useState("");
    const [popupErrorMessage, setPopupErrorMessage] = useState("");
    const [popupSuccessMessage, setPopupSuccessMessage] = useState("");

    const handleSearch = () => {
        if (setSearch) setSearch(searchInput);
        if (setPage) setPage(1);
    };

    const handleSearchKeyPress = (e) => {
        if (e.key === "Enter") handleSearch();
    };

    const confirmDelete = (row) => {
        setCheckToDelete(row);
        setShowDeleteConfirm(true);
    };

    const closeDeleteModal = () => {
        setShowDeleteConfirm(false);
        setCheckToDelete(null);
    };

    const handleDelete = async () => {
        setShowDeleteConfirm(false);
        if (!checkToDelete || !onDeleteCheck) return;

        try {
            setIsProcessingPopupVisible(true);
            setProcessingMessage("Deleting check...");
            await onDeleteCheck(checkToDelete);
            setPopupSuccessMessage("Check deleted successfully");
        } catch (e) {
            if (e.response?.data?.status === "NotAuthorized") {
                authNavigate("/login", "iam", navigate);
                return;
            }
            setPopupErrorMessage(
                e.response?.data?.message || e?.message || "Failed to delete check"
            );
        } finally {
            setIsProcessingPopupVisible(false);
            setProcessingMessage("");
            setCheckToDelete(null);
        }
    };

    const totalPages = Math.ceil((total || 0) / (size || 25)) || 1;

    const normalizeAccessUsers = (row) => {
        const users = row.genai_rights_access || row.users || [];
        if (!Array.isArray(users)) return [];
        return users
            .map((x) => {
                if (!x) return null;
                if (typeof x === "string") return { email: x };
                if (x.email) return { email: x.email };
                return null;
            })
            .filter(Boolean);
    };

    const visible = checks.filter(
        (row) => statusFilter === "ALL" || (row.status || "RUNNING") === statusFilter
    );

    return (
        <div className="ev-card-4 w-100 ev-padding-large">
            <div className="row mt-1 mb-3 align-items-center">
                <div className="col-6 position-relative">
                    <input
                        type="text"
                        className="form-control search-input ms-0"
                        placeholder="Search by name or file"
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        onKeyPress={handleSearchKeyPress}
                    />
                    <button
                        type="button"
                        className="ev-btn-lightblue-fill-round search-bar-button"
                        onClick={handleSearch}
                        title="Search"
                    >
                        <i className="bi bi-search"></i>
                    </button>
                </div>

                <div className="col-6 d-flex justify-content-end align-items-center gap-2">
                    <button
                        type="button"
                        className="ev-btn-lightblue-fill-round ev-btn-small ev-bold-med"
                        onClick={onRefresh}
                        disabled={isProcessingPopupVisible}
                    >
                        <i className="bi bi-arrow-repeat" style={{ animation: "none" }}></i> Refresh
                    </button>

                </div>
            </div>

            {/* Table */}
            <div className="table-container">
                <table className="table-custom1 mt-0 w-100" style={{ tableLayout: "fixed" }}>
                    <thead>
                        <tr>
                            <th className="w-20"><label>Check Name</label></th>
                            <th className="w-10 text-center"><label>Owner</label></th>
                            <th className="w-15 text-center"><label>File</label></th>
                            <th className="w-10 text-nowrap ps-4">
                                <StatusFilter
                                    value={statusFilter}
                                    onChange={(val) => {
                                        setStatusFilter(val);
                                        if (setPage) setPage(1);
                                    }}
                                />
                            </th>
                            <th className="w-12 text-center"><label>Shared With</label></th>
                            <th className="w-13 ps-4"><label>Created At</label></th>
                            <th className="w-5 text-center"><label>Papers</label></th>
                            <th className="w-15 text-center"><label>User Actions</label></th>
                        </tr>
                    </thead>
                </table>

                <div className="scrollable-element dashboard-right-contentarea-table-height-1">
                    <table className="table-custom1 overflow-hidden w-100" style={{ tableLayout: "fixed" }}>
                        <tbody>
                            {visible.length === 0 ? (
                                <tr>
                                    <td colSpan="8" className="text-center py-5">
                                        <p className="ev-small-p-blue mb-0">No rights checks found</p>
                                    </td>
                                </tr>
                            ) : (
                                visible.map((row) => (
                                    <tr key={row.id}>
                                        <td className="align-top w-20">
                                            <div className="ev-small-p-dark mb-0 avatar-tooltip mb-1" data-tooltip={row.name}>
                                                &nbsp;<TrimContent text={row.name || ""} maxLength={40} />
                                            </div>
                                        </td>

                                        <td className="align-top w-10 text-center avatar-tooltip" data-tooltip={row.owner}>
                                            <div className="ev-message-card5 ev-fs-11 text-truncate d-inline-block"
                                                style={{ maxWidth: "100%" }}>
                                                <TrimContent text={row.owner || ""} maxLength={22} />
                                            </div>
                                        </td>

                                        <td className="align-top w-15 text-center avatar-tooltip" data-tooltip={row.file_name}>
                                            <span className="ev-small-p-dark d-inline-flex align-items-center text-truncate" style={{ maxWidth: "100%" }}>
                                                <i className="bi bi-file-earmark-text me-1 opacity-50 flex-shrink-0"></i>
                                                <span className="text-truncate">
                                                    <TrimContent text={row.file_name || ""} maxLength={20} />
                                                </span>
                                            </span>
                                        </td>

                                        <td className="align-top w-10">
                                            <span className={`ev-badge-pill ev-fs-11 ${STATUS_CONFIG[row.status]?.badgeClass || "ev-badge-blue"}`}>
                                                {STATUS_CONFIG[row.status]?.label || row.status || "Running"}
                                            </span>
                                            {row.status === "RUNNING" && row.total_rows > 0 && (
                                                <div className="ev-xsmall-p-dark mt-1">
                                                    {row.processed_rows ?? 0} / {row.total_rows}
                                                </div>
                                            )}
                                        </td>

                                        <td className="align-top w-12">
                                            <div className="d-flex justify-content-center">
                                                {normalizeAccessUsers(row).length > 0 ? (
                                                    <UserCircleDisplay
                                                        users={normalizeAccessUsers(row)}
                                                        maxDisplay={4}
                                                    />
                                                ) : (
                                                    <span className="ev-small-p-dark">None</span>
                                                )}
                                            </div>
                                        </td>

                                        <td className="align-top w-13">
                                            {row.created_at ? formatDateTime(row.created_at) : "—"}
                                        </td>

                                        <td className="align-top w-5 text-center">
                                            <span className="ev-reg-p-blue ev-bold-med">{row.total_rows ?? 0}</span>
                                        </td>

                                        <td className="align-top w-15">
                                            <div className="d-flex justify-content-end align-items-center gap-2">
                                                <button
                                                    className="ev-btn-blue-outline-round ev-btn-small text-nowrap"
                                                    type="button"
                                                    onClick={() => onOpenDashboard && onOpenDashboard(row)}
                                                >
                                                    Rights Dashboard
                                                </button>
                                                <button
                                                    className="btn ev-btn-red-fill-round ev-btn-med ev-radius-xsmall pe-2 ps-2 avatar-tooltip"
                                                    type="button"
                                                    onClick={() => confirmDelete(row)}
                                                    data-tooltip="Delete Check"
                                                >
                                                    <i className="bi bi-trash"></i>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="d-flex justify-content-between align-items-center mt-3">
                <div className="ev-small-p-dark">
                    Showing {visible.length} of {total} entries
                </div>
                <div className="d-flex gap-2 align-items-center">
                    <button
                        className="ev-btn-light-fill"
                        disabled={page === 1}
                        onClick={() => setPage && setPage(page - 1)}
                        type="button"
                    >
                        <i className="bi bi-chevron-left"></i>
                    </button>
                    <button className="ev-btn-light-fill" disabled type="button">
                        Page {page} of {totalPages}
                    </button>
                    <button
                        className="ev-btn-light-fill"
                        disabled={page >= totalPages}
                        onClick={() => setPage && setPage(page + 1)}
                        type="button"
                    >
                        <i className="bi bi-chevron-right"></i>
                    </button>
                </div>
                <div>
                    <PaginationControls
                        initialSize={size}
                        onSizeChange={(newSize) => {
                            if (setSize) setSize(newSize);
                            if (setPage) setPage(1);
                        }}
                    />
                </div>
            </div>

            {isProcessingPopupVisible && (
                <ProcessingPopup message={processingMessage || "Working..."} />
            )}

            {popupErrorMessage && (
                <ErrorPopup onClose={() => setPopupErrorMessage("")} message={popupErrorMessage} />
            )}

            {popupSuccessMessage && (
                <SuccessPopup onClose={() => setPopupSuccessMessage("")} message={popupSuccessMessage} />
            )}

            <DeleteConfirmationModal
                show={showDeleteConfirm}
                onClose={closeDeleteModal}
                onConfirm={handleDelete}
                objectName="Rights check"
                itemName={checkToDelete?.name ? String(checkToDelete.name).slice(0, 40) : ""}
                bodyContent={
                    <div>
                        <p className="mb-0">This will permanently delete this rights check and all of its results.</p>
                        <p className="text-danger mb-0">This action cannot be undone.</p>
                    </div>
                }
            />
        </div>
    );
};

export default GenAIRightsList;